import React, { useCallback, useEffect, useRef, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Send, Search, MessagesSquare, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/hooks/useRealtime";
import { toast } from "sonner";

const ROLE_TONES = {
  admin: "bg-red-500", directeur: "bg-violet-500", chef: "bg-blue-600",
  superviseur: "bg-indigo-500", agent: "bg-emerald-500", auditeur: "bg-slate-500",
};

function initials(name) {
  return (name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
}

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

export default function Messagerie() {
  const { user } = useAuth();
  const isWriter = user?.role !== "auditeur";
  const [conversations, setConversations] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null); // other user
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [q, setQ] = useState("");
  const listRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/dm");
      setConversations(data);
    } catch (err) {
      console.warn("[Messagerie] loadConversations failed", err);
    }
  }, []);

  const loadThread = useCallback(async (otherId) => {
    if (!otherId) return;
    try {
      const { data } = await api.get(`/messages/dm/with/${otherId}`);
      setMessages(data.messages || []);
    } catch (err) {
      console.warn("[Messagerie] loadThread failed", err);
    }
  }, []);

  useEffect(() => {
    loadConversations();
    api.get("/users").then((r) => setUsers(r.data)).catch((err) => {
      console.warn("[Messagerie] loadUsers failed", err);
    });
  }, [loadConversations]);

  useEffect(() => {
    if (selected) loadThread(selected.id);
  }, [selected, loadThread]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  useRealtime((type, data) => {
    if (type !== "message") return;
    if (data?.entity_type === "direct") {
      loadConversations();
      if (selected) {
        const conv = [user.id, selected.id].sort().join("__");
        if (data.entity_id === conv) loadThread(selected.id);
      }
    }
  });

  const send = async (e) => {
    e?.preventDefault();
    if (!content.trim() || !selected) return;
    setSending(true);
    try {
      await api.post("/messages/dm", { to_user_id: selected.id, content });
      setContent("");
      loadThread(selected.id);
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSending(false);
    }
  };

  const startWith = (u) => {
    setSelected(u);
  };

  const searchable = users.filter((u) => u.id !== user?.id).filter((u) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return u.name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s);
  });

  return (
    <div>
      <PageHeader
        eyebrow="Collaboration"
        title="Messagerie"
        description="Discussions directes entre membres de l'équipe — temps réel."
        testid="messagerie-header"
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[calc(100vh-16rem)] min-h-[520px]">
        {/* Left column: conversations + user picker */}
        <Card className="border border-slate-200 bg-white md:col-span-1 flex flex-col overflow-hidden">
          <div className="p-3 border-b border-slate-200">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Chercher un collègue..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="dm-search-user" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length > 0 && (
              <div>
                <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.28em] text-slate-400">Conversations</div>
                {conversations.map((c) => {
                  const o = c.other || {};
                  const active = selected?.id === o.id;
                  return (
                    <button
                      key={c.conversation_id}
                      onClick={() => startWith(o)}
                      className={`w-full text-left px-3 py-2.5 flex items-center gap-3 border-b border-slate-100 ${active ? "bg-blue-50" : "hover:bg-slate-50"}`}
                      data-testid={`dm-conv-${o.id}`}
                    >
                      <div className={`shrink-0 w-9 h-9 rounded-full text-white text-xs font-semibold grid place-items-center ${ROLE_TONES[o.role] || "bg-slate-500"}`}>
                        {initials(o.name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-semibold text-slate-900 truncate">{o.name}</div>
                          <div className="text-[10px] text-slate-400 font-mono shrink-0">{timeAgo(c.last_at)}</div>
                        </div>
                        <div className="text-xs text-slate-500 truncate">{c.last_by_name}: {c.last_content}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            <div>
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.28em] text-slate-400">Tous les collègues</div>
              {searchable.map((u) => (
                <button
                  key={u.id}
                  onClick={() => startWith(u)}
                  className={`w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-slate-50 border-b border-slate-100 ${selected?.id === u.id ? "bg-blue-50" : ""}`}
                  data-testid={`dm-user-${u.id}`}
                >
                  <div className={`shrink-0 w-9 h-9 rounded-full text-white text-xs font-semibold grid place-items-center ${ROLE_TONES[u.role] || "bg-slate-500"}`}>
                    {initials(u.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{u.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      <Badge variant="outline" className="mr-1 text-[9px] px-1.5 py-0">{u.role}</Badge>
                      {u.service || u.email}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </Card>

        {/* Right: active thread */}
        <Card className="border border-slate-200 bg-white md:col-span-2 flex flex-col overflow-hidden">
          {!selected ? (
            <div className="flex-1 grid place-items-center text-slate-400 text-center p-8">
              <div>
                <MessagesSquare className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <div className="text-lg text-slate-500">Sélectionnez un collègue</div>
                <div className="text-sm text-slate-400 mt-1">pour démarrer une conversation directe.</div>
              </div>
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
                <button onClick={() => setSelected(null)} className="md:hidden text-slate-500 hover:text-slate-800">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div className={`w-10 h-10 rounded-full text-white text-sm font-semibold grid place-items-center ${ROLE_TONES[selected.role] || "bg-slate-500"}`}>
                  {initials(selected.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg font-semibold text-slate-900 truncate" data-testid="dm-header-name">{selected.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">{selected.role}</Badge>
                    {selected.service && <span>· {selected.service}</span>}
                  </div>
                </div>
              </div>

              <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-white" data-testid="dm-list">
                {messages.length === 0 ? (
                  <div className="text-center text-slate-400 text-sm py-12">
                    Aucun message. Commencez la conversation !
                  </div>
                ) : messages.map((m) => {
                  const mine = m.user_id === user?.id;
                  return (
                    <div key={m.id} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`} data-testid={`dm-msg-${m.id}`}>
                      <div className={`shrink-0 w-8 h-8 rounded-full text-white text-xs font-semibold grid place-items-center ${ROLE_TONES[m.user_role] || "bg-slate-500"}`}>
                        {initials(m.user_name)}
                      </div>
                      <div className={`max-w-[70%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                        <div className={`text-xs text-slate-500 mb-0.5 ${mine ? "text-right" : ""}`}>
                          <span className="font-semibold text-slate-700">{m.user_name}</span>
                          <span className="ml-2 font-mono">{timeAgo(m.created_at)}{m.edited_at && " · modifié"}</span>
                        </div>
                        <div className={`rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                          mine ? "bg-blue-800 text-white rounded-tr-sm" : "bg-slate-100 text-slate-800 rounded-tl-sm"
                        } ${m.deleted ? "italic opacity-60" : ""}`}>
                          {m.content}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {isWriter && (
                <form onSubmit={send} className="border-t border-slate-200 p-3 flex items-end gap-2" data-testid="dm-form">
                  <Textarea
                    rows={2}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    placeholder={`Message à ${selected.name}...`}
                    className="flex-1 resize-none text-sm"
                    data-testid="dm-input"
                  />
                  <Button type="submit" disabled={sending || !content.trim()} className="bg-blue-900 hover:bg-blue-800 h-11 shrink-0" data-testid="dm-send-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
